/**
 * The advisor email-verification link: constants, the URL builder, and the
 * trust window it establishes.
 *
 * WHY THIS EXISTS
 *
 * The conversational operations API identifies an advisor from
 * `X-Waterlily-Verified-Subject`. Over Messenger that header is trustworthy —
 * the web app boots Intercom with an HMAC identity hash, so Intercom itself
 * verified it. Over **email it is not**: a From address is text in a header and
 * anyone can forge it. Without a second proof, a forged From would reach real
 * client health data.
 *
 * This link is that second proof, and it works the way a password reset does.
 * Fin never emails the link anywhere new — it replies on the existing Intercom
 * thread, which is delivered to the address being *claimed*. So someone forging
 * `advisor@firm.com` causes a link to land in the real advisor's inbox and
 * cannot use it themselves. Clicking it is therefore proof of mailbox control.
 *
 * HOW IT FLOWS
 *
 *   server  `createAdvisorSignInLink` mints a single-use Clerk sign-in token
 *        -> `advisorEmailVerifyLink` wraps it in this URL
 *        -> Fin puts the URL in its reply
 *   client  ADVISOR_EMAIL_VERIFY_PATH redeems the token on an explicit click; no sign-in
 *        -> the page calls back to record the verification
 *   server  a row lands in `AdvisorEmailVerifications`, and the conversation API
 *           consults that row on every later email request
 *
 * This module mirrors `clientAutoSignInLink` deliberately — same fragment-based
 * ticket handling, same Clerk token semantics — but stays a separate route and
 * builder because the audience, the copy and the post-redemption side effect all
 * differ. The two must not be merged.
 */

import { getBaseUrl } from '@shared';
import { AUTO_SIGN_IN_TICKET_PARAM } from './clientAutoSignInLink';

/**
 * Route that redeems the ticket and records the verification.
 *
 * Must be registered as a PUBLIC route. The recipient is signed out when they
 * click, so an authenticated route would bounce them before the ticket could be
 * redeemed.
 */
export const ADVISOR_EMAIL_VERIFY_PATH = '/auth/email-verify';

/**
 * The ticket and email parameter names are REUSED from the client auto-sign-in flow rather than
 * redeclared here (corrected 9 Sep 2026).
 *
 * This link is built here but redeemed by `useTicketSignIn`, which the advisor verification page
 * shares with the client flow — and that hook reads `AUTO_SIGN_IN_TICKET_PARAM`. Declaring a
 * separate `ADVISOR_VERIFY_TICKET_PARAM` with the same literal meant writer and reader agreed only
 * by coincidence: changing one would have broken verification silently, with the page reporting an
 * invalid link and nothing pointing at the cause. One constant, one source of truth.
 */

/** Correlates the click back to the Intercom thread that asked for it. Audit only. */
export const ADVISOR_VERIFY_CONVERSATION_PARAM = 'conversation_id';

/**
 * How long a single verification is trusted for.
 *
 * Chosen 7 Sep 2026 as "everything for 30 days": one click covers every thread
 * and every operation until it lapses. The alternatives considered were a
 * read/write split (writes needing fresher proof) and per-thread verification;
 * both were rejected as too much friction for the first build. The trade-off
 * accepted is that a forged From address within an open window inherits the
 * verified advisor's access, which is why revocation exists on the table.
 */
export const ADVISOR_EMAIL_TRUST_DAYS = 30;
/** Internal to `advisorEmailTrustExpiry`; callers use ADVISOR_EMAIL_TRUST_DAYS. */
const ADVISOR_EMAIL_TRUST_MS = ADVISOR_EMAIL_TRUST_DAYS * 24 * 60 * 60 * 1000;

/**
 * How long the emailed link itself stays usable, which is a different and much
 * shorter clock than the trust window above. Clerk enforces this server-side;
 * this is only the value we request when minting.
 */
export const ADVISOR_VERIFY_TICKET_EXPIRY_SECONDS = 3600;
export const ADVISOR_VERIFY_TICKET_EXPIRY_MINUTES =
  ADVISOR_VERIFY_TICKET_EXPIRY_SECONDS / 60;

/**
 * The fragment key the verification token travels under.
 *
 * Deliberately NOT `AUTO_SIGN_IN_TICKET_PARAM` (`__clerk_ticket`). This token is not a Clerk ticket
 * and must never be handed to Clerk. A name of its own means nothing that recognises a sign-in
 * ticket — the client auto-sign-in hook, the log redactors — can mistake one for the other.
 */
export const ADVISOR_VERIFY_TOKEN_PARAM = 'verify_token';

export function advisorEmailVerifyLink({
  token,
  conversationId,
}: {
  token: string;
  conversationId?: string | null;
}): string {
  const params = new URLSearchParams();
  if (conversationId) params.set(ADVISOR_VERIFY_CONVERSATION_PARAM, conversationId);

  // The token goes in the FRAGMENT, never the query string. Fragments are not sent to servers, so
  // it stays out of access logs, proxy logs and Referer headers. The page reads it and strips it
  // from the address bar on first paint.
  //
  // The email address is no longer carried. It prefilled a manual sign-in fallback for a lapsed
  // Clerk ticket; with no sign-in there is no fallback, and an address in a URL bought nothing.
  const fragment = new URLSearchParams({ [ADVISOR_VERIFY_TOKEN_PARAM]: token });
  const query = params.toString();
  return `${getBaseUrl()}${ADVISOR_EMAIL_VERIFY_PATH}${
    query ? `?${query}` : ''
  }#${fragment.toString()}`;
}

/**
 * Replaces any verification token in `text` with `[redacted]`.
 *
 * For anywhere a verification email body is stored or replayed. The magic email agent records the
 * reply it sent as a transcript turn and replays recent turns to OpenAI, so an unredacted body puts
 * a live single-use proof-of-mailbox into both the database and a third party's API. The legacy
 * `__clerk_ticket` form is covered too, in case an older link is ever quoted back.
 *
 * The value pattern is the base64url alphabet plus `.`, which spans both token shapes; it stops at
 * the first character that cannot belong to one, so surrounding text is left intact.
 */
export function redactVerificationLinks(text: string): string {
  const pattern = new RegExp(
    `((?:${ADVISOR_VERIFY_TOKEN_PARAM}|${AUTO_SIGN_IN_TICKET_PARAM})=)[A-Za-z0-9._-]+`,
    'g',
  );
  return text.replace(pattern, '$1[redacted]');
}

/** The expiry instant for a verification recorded now. */
export function advisorEmailTrustExpiry(fromMs: number = Date.now()): Date {
  return new Date(fromMs + ADVISOR_EMAIL_TRUST_MS);
}
